import { ApiResponse, PaginatedData } from '@/types/api';
import { Company, Member } from '@/types/user';
import { Document } from './document';
import { Transaction } from './bank';

export enum LoanAgreementType {
  FROM_COMPANY = 'from_company',
  TO_COMPANY = 'to_company',
  FROM_MEMBER = 'from_member',
  ACTIVE = 'active' // this type is only for filter
}

export enum LoanAgreementStatus {
  CREATED = 'created',
  STARTED = 'started',
  IN_PROGRESS = 'in_progress',
  CONCLUDED = 'concluded',
  DELETED = 'deleted'
}

export interface LoanAgreement {
  company: Company;
  agreement_date: string;
  repayment_date: string;
  starting_date: string;
  end_date: string;
  created: string;
  currency: string;
  _id: string;
  amount_borrowed: number;
  number_installments: number;
  installment_interest: number;
  installment_principal: number;
  installment_total: number;
  interest: number;
  loan_agreement: Document;
  recipient: {
    name: string;
    company_address: string;
    country: string;
    email: string;
    phone_number: string;
    representative_name: string;
    representative_tax_id: string;
    tax_id: string;
    appears_as: string[];
    member?: Member;
  };
  status: LoanAgreementStatus;
  type: LoanAgreementType;
  total_amount: number;
  transactions_ids: string[];
}

export type GetLoanAgreementsPaginatedResponse = ApiResponse<{
  data: {
    company_loan_agreements: LoanAgreement[];
  } & PaginatedData;
}>;

export interface CalculateTotalAmountLoanAgreementPayload {
  amount: number;
  installments: number;
  rate: number;
  startingDate: Date;
}

export type CalculateTotalAmountLoanAgreementResponse = ApiResponse<{
  total_loan: {
    end_date: string;
    installment_interest: number;
    installment_principal: number;
    installment_total: number;
    total_amount: number;
    total_interest: number;
  };
}>;

export type SendLoanAgreementToEmailResponse = ApiResponse<{
  sent: boolean;
}>;

export interface CreateLoanAgreementFromMemberToCompanyPayload {
  amount: number;
  currency: string;
  installments: number;
  interest: number;
  member: string;
  agreement_date: Date;
  starting_date: Date;
}

export type CreateLoanAgreementFromMemberToCompanyResponse = ApiResponse<{
  agreement: LoanAgreement;
}>;

export interface CreateLoanAgreementLendToCompanyPayload {
  amount: number;
  currency: string;
  installments: number;
  interest: number;
  agreement_date: Date;
  starting_date: Date;
  borrower: {
    appears_as: string[];
    company_address: string;
    country: string;
    email: string;
    name: string;
    phone_number: string;
    representative_name: string;
    representative_tax_id: string;
    tax_id: string;
  };
}

export type CreateLoanAgreementLendToCompanyResponse = ApiResponse<{
  agreement: LoanAgreement;
}>;

export interface CreateLoanAgreementBorrowFromCompanyPayload {
  amount: number;
  currency: string;
  installments: number;
  interest: number;
  agreement_date: Date;
  starting_date: Date;
  lender: {
    appears_as: string[];
    company_address: string;
    country: string;
    email: string;
    name: string;
    phone_number: string;
    representative_name: string;
    representative_tax_id: string;
    tax_id: string;
  };
}

export type CreateLoanAgreementBorrowFromCompanyResponse = ApiResponse<{
  agreement: LoanAgreement;
}>;

export type GetLoanAgreementByIdResponse = ApiResponse<{ agreement: LoanAgreement }>;

export interface GetLoanAgreementsByTypeRequest {
  types: LoanAgreementType[];
}

export type GetLoanAgreementsByTypeResponse = ApiResponse<{ agreements: LoanAgreement[] }>;

export interface LoanAgreementMatchedTransactionsFilters {
  bank?: string;
  // balances (account.account_id) to exclude
  account_id?: string[];
  status?: string[];
  matching_type?: string[];
}

export interface LoanAgreementMatchedTransactionsAccount {
  account_id: string;
  description: string;
  currency: string;
}

export type GetLoanAgreementMatchedTransactionsResponse = ApiResponse<{
  data: {
    transactions: Transaction[];
    // banks that have payments matched to the agreement
    bank_ids: string[];
    // balances of the selected bank that have payments matched to the agreement
    accounts: LoanAgreementMatchedTransactionsAccount[];
  } & PaginatedData;
}>;
